/**
 * Two small, independent progressive enhancements:
 *
 * - Scroll reveal: elements carrying [data-reveal] start hidden (opacity 0,
 *   translateY) under html.js via base.css; once at least 15% of one enters
 *   the viewport this adds .is-revealed (CSS transitions it in) and stops
 *   observing it. If IntersectionObserver isn't available every [data-reveal]
 *   element is revealed immediately instead of staying permanently hidden.
 *
 * - Media skeleton: elements carrying [data-media-skeleton] show a shimmer
 *   (base.css) until the <img> inside finishes loading (or errors, so a
 *   broken image doesn't shimmer forever), then get .is-loaded.
 *
 * IT MUST RUN AGAIN ON NEW MARKUP, AND FOR A LONG TIME IT DID NOT.
 *
 * This file owns the ONLY code in the theme that ever adds .is-revealed, while
 * base.css hides every [data-reveal] element and every heading unconditionally
 * under html.js. So anything that enters the DOM after the first run - and is
 * never processed - is not merely un-animated. It is invisible, permanently,
 * until a full page load.
 *
 * Two things put markup on the page after load, and both were broken:
 *
 *   - The theme editor re-renders a whole section on EVERY block add, remove,
 *     reorder or setting change, then fires shopify:section:load. Sections
 *     whose [data-reveal] wraps all their content - promo-bento, brand-lab,
 *     reviews, newsletter - therefore vanished the moment a merchant touched
 *     them, and stayed gone.
 *   - assets/facets.js replaces the product grid on pagination and filtering.
 *     Every product card carries [data-reveal], so page two of any collection
 *     rendered completely blank while sitting fully present in the DOM.
 *
 * Ten other scripts in this theme already listen for shopify:section:load.
 * This one was the omission.
 *
 * window.ThemeReveal.init(root) is exposed so facets.js can call it directly
 * after a swap - a DOM replacement fires no event, so there is nothing to
 * listen for there.
 */
(function () {
  var observer = null;

  /* Elements waiting on the geometric sweep. Module-scoped, because init() can
     be called repeatedly and each call must ADD to the set rather than replace
     it - a second section loading must not abandon the first one's pending
     elements. */
  var pending = [];
  var queued = false;
  var sweeping = false;

  function markLoaded(media) {
    media.classList.add('is-loaded');
  }

  function initSkeletons(root) {
    root.querySelectorAll('[data-media-skeleton]').forEach(function (media) {
      if (media.classList.contains('is-loaded')) return;
      var img = media.querySelector('img');
      if (!img || img.complete) {
        markLoaded(media);
        return;
      }
      img.addEventListener('load', function () { markLoaded(media); }, { once: true });
      img.addEventListener('error', function () { markLoaded(media); }, { once: true });
    });
  }

  /* The masked slide-up.
   *
   * A heading rises from BEHIND its own bottom edge rather than fading in, so
   * the words appear to be uncovered rather than to materialise. That needs
   * two elements: the heading clips (base.css gives it overflow: hidden) and
   * an inner span does the moving. Clip and movement cannot share one element
   * - overflow is in the element's own coordinate space, so translating the
   * heading carries its own mask along with it and nothing is ever revealed.
   *
   * The span is added here rather than in Liquid because these are ordinary
   * h1/h2/h3 tags all over the theme, and requiring every section to wrap its
   * heading by hand is a rule that gets forgotten by the third new section.
   *
   * Child NODES are moved, not innerHTML copied: a heading may legitimately
   * contain a link, a <br> or a highlight span, and reassigning innerHTML
   * would rebuild those elements and drop any listener bound to them.
   *
   * ONLY headings this script will itself reveal are masked. A mask is a
   * promise to open it, and hiding a heading whose reveal depends on machinery
   * outside this file means that if that machinery never runs, the words stay
   * behind the mask permanently. Several sections - brand-lab, promo-bento,
   * shop-by-spec, discord-cta - set opacity on their own intro block at a
   * specificity that beats [data-reveal].is-revealed and drive it from their
   * own scroll code; masking those would have left four home page headings
   * blank.
   *
   * Card titles are excluded too. They are h3s, so they match the selector,
   * but twenty products sliding their titles out of twenty little masks is a
   * screen full of fidget, and the title sits inside a link with its own hover
   * motion that the mask would clip. */
  var SELF_ANIMATED = [
    'hero-carousel__heading',
    'banner-mosaic__heading',
    'banner-reveal__heading',
    'video-slide__heading',
    'video-scroll__heading',
    /* Clipped or transformed by their own section, so IntersectionObserver
       never reports them as visible and .is-revealed never arrives. A mask
       they can never climb out of is worse than no mask at all. */
    'drop-reveal__heading',
    'perfect-match__heading',
    'tabbed-products__heading'
  ];

  function maskHeading(heading) {
    if (heading.querySelector('.heading-rise__inner')) return;
    if (heading.classList.contains('product-card__title')) return;
    if (heading.closest('[data-heading-no-mask]')) return;

    for (var i = 0; i < SELF_ANIMATED.length; i++) {
      if (heading.classList.contains(SELF_ANIMATED[i])) return;
    }

    var inner = document.createElement('span');
    inner.className = 'heading-rise__inner';
    while (heading.firstChild) {
      inner.appendChild(heading.firstChild);
    }
    heading.appendChild(inner);
    heading.classList.add('heading-rise');
  }

  /* Both the element itself and its descendants.
   *
   * shopify:section:load hands us the section wrapper, and that wrapper can BE
   * a [data-reveal] element rather than merely contain one. querySelectorAll
   * alone would skip it, which is the difference between a section appearing
   * and a section staying blank. */
  function collect(root, selector) {
    var found = Array.prototype.slice.call(root.querySelectorAll(selector));
    if (root.matches && root.matches(selector)) found.unshift(root);
    return found;
  }

  function reveal(el) {
    el.classList.add('is-revealed');
    if (observer) observer.unobserve(el);
  }

  function sweep() {
    queued = false;
    /* Re-entrancy guard: reveal() can trigger layout work that leads back here
       through a resize, and a sweep running inside a sweep would iterate a
       list the outer call is still mutating. */
    if (sweeping) return;
    sweeping = true;

    var remaining = [];
    for (var i = 0; i < pending.length; i++) {
      var el = pending[i];
      if (el.classList.contains('is-revealed')) continue;
      if (!el.isConnected) continue; /* removed from the DOM; stop tracking it */

      var rect = el.getBoundingClientRect();
      /* The same threshold the observer uses, expressed as geometry: the
         element has entered if its top has passed the viewport's bottom edge
         less the 40px the observer trims, and it has not yet left out of the
         top. */
      var enteredFromBelow = rect.top < window.innerHeight - 40;
      /* Above the viewport counts as revealed, not as "not yet arrived" -
         same reasoning as the observer callback above. Gating on
         `rect.bottom > 0` kept anything the reader had already scrolled past
         pending forever, which on this path meant it was never shown at all. */
      var goneAbove = rect.bottom <= 0;

      if ((enteredFromBelow || goneAbove) && rect.height > 0) {
        reveal(el);
      } else {
        remaining.push(el);
      }
    }

    pending = remaining;
    sweeping = false;

    /* The listeners are NEVER removed, even when nothing is pending.
       An earlier version tore them down once the set emptied, which worked
       perfectly until the theme editor injected a section afterwards - by then
       every mechanism was dead and the new markup could never be revealed.
       Two idle listeners cost nothing; a dead reveal mechanism costs the whole
       section. */
  }

  function request() {
    /* Coalesced onto a frame: scroll fires far faster than the screen
       refreshes, and measuring a rect forces layout - doing that per event
       rather than per frame is how a scroll handler becomes the reason a page
       stutters. */
    if (queued) return;
    queued = true;
    window.requestAnimationFrame(sweep);
  }

  /**
   * Process a subtree. Safe to call repeatedly and on overlapping roots -
   * everything it does is idempotent, so a section processed twice is
   * unchanged the second time.
   */
  function init(root) {
    var scope = root || document;
    if (scope.nodeType !== 1 && scope.nodeType !== 9) return;

    initSkeletons(scope);

    /* Headings inside a [data-reveal] wrapper are skipped: the wrapper already
       carries them, and two rises on one element stack into a lurch. */
    var headings = collect(scope, 'main :is(h1, h2, h3)').filter(function (el) {
      return !el.closest('[data-reveal]');
    });
    headings.forEach(maskHeading);

    var targets = collect(scope, '[data-reveal]')
      .concat(headings)
      .filter(function (el) {
        return !el.classList.contains('is-revealed');
      });

    if (!targets.length) return;

    /* No observer: reveal everything at once. Something that stayed hidden
       because the mechanism to show it is missing is far worse than something
       that never animated. */
    if (!observer) {
      targets.forEach(reveal);
      return;
    }

    targets.forEach(function (el) {
      observer.observe(el);
      pending.push(el);
    });

    /* Swept immediately rather than on the next scroll. New markup is
       frequently already in view - a re-rendered section in the theme editor
       always is - and waiting for a scroll that may never come is how it stays
       blank. */
    request();
  }

  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(
      function (entries, obs) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) {
            /* Already scrolled PAST, not yet arrived. A reload restores the
               previous scroll position and an anchored link lands mid-page,
               and in both cases everything above that point is reported as
               not intersecting exactly once and never again - the reader has
               no reason to scroll back up, so it stays hidden for good. On a
               product page that meant the product's own title, parked a full
               line below its mask with nothing to bring it back.

               There is no entrance to play for something behind the reader,
               so it is simply put in its final state. */
            if (entry.boundingClientRect.bottom <= 0) {
              entry.target.classList.add('is-revealed');
              obs.unobserve(entry.target);
            }
            return;
          }
          entry.target.classList.add('is-revealed');
          obs.unobserve(entry.target);
        });
      },
      /* Reveal BEFORE the eye arrives, not after: the old 15%-visible +
         -40px inset meant content started its 500ms entrance only once the
         reader was already looking at the gap, which read as sections
         loading slowly. A positive bottom margin starts the entrance while
         the element is still ~12% of a viewport below the fold, so by the
         time it scrolls into view it is already there. */
      { threshold: 0, rootMargin: '0px 0px 12% 0px' }
    );
  }

  /* THE CLIPPED-ANCESTOR SAFETY NET.
   *
   * IntersectionObserver measures an element's rect after its ancestors have
   * clipped it, so an element inside a section that clips or transforms its
   * own contents reports zero intersection even while centred on the screen -
   * the observer never fires and .is-revealed never arrives. That is not
   * hypothetical: it left four headings permanently invisible on the home
   * page, each in a different section, and an allow-list of section names
   * would need extending every time somebody adds a section that clips.
   *
   * getBoundingClientRect does NOT account for ancestor clipping, so the same
   * question can be asked geometrically and answered correctly in exactly the
   * cases the observer gets wrong. Both run together; whichever fires first
   * wins, because .is-revealed is idempotent. */
  window.addEventListener('scroll', request, { passive: true });
  window.addEventListener('resize', request, { passive: true });

  /* The theme editor re-renders an entire section on every block add, remove,
     reorder and setting change. Without this the section comes back with no
     .is-revealed on anything and is invisible until a full reload - which is
     what made promo-bento, brand-lab, reviews and newsletter "disappear". */
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });

  /* Selecting a block scrolls it into view and can reveal markup that was
     never observed - cheap insurance, and it costs one idempotent pass. */
  document.addEventListener('shopify:block:select', function (event) {
    init(event.target);
  });

  /* For DOM replaced by script rather than by Shopify. A replaceWith() fires
     no event, so facets.js has to say so itself after swapping the grid. */
  window.ThemeReveal = { init: init };

  init(document);
})();
